import * as React from 'react';

import {WindowContext} from '../contexts/WindowContext';

import {Theme} from './types';
import {parseThemeConfig, createPropertyName} from './utils';
import {addThemeListener, getCurrentTheme} from './api';
import {defaultTheme} from './themes/default';

export interface ThemeContext {
    theme: React.RefObject<Theme>;
    setTheme: (theme: Theme) => void;
}

// The default context used for theme providers if none is set.
export const DefaultThemeContext: React.Context<ThemeContext> = React.createContext({
    theme: {} as React.RefObject<Theme>,
    setTheme: (theme: Theme) => {}
});

export interface Theming {
    // Unique context for this theme to access it anywhere in the tree.
    context: React.Context<ThemeContext>;
    // Hook to get the theme being used by a component.
    useTheme: () => ThemeContext;
    // ThemeProvider connected with a context, useTheme & updateTheme.
    ThemeProvider: React.FC<ThemeProviderProps>;
}

export function createTheming(
    theme: Theme | undefined,
    updateBody: boolean = false,
    autoConnect = true,
    customContext?: React.Context<ThemeContext>
): Theming {
    const context = customContext || DefaultThemeContext;
    // eslint-disable-next-line new-cap
    const provider: React.FC<ThemeProviderProps> = (props) => ThemeProvider({
        theme,
        updateBody,
        autoConnect,
        ...props,
        Context: context
    });
    const useTheme = () => React.useContext(context);

    return {
        context,
        useTheme,
        ThemeProvider: React.memo(provider)
    };
}

interface ThemeProviderProps {
    updateBody?: boolean;
    theme?: Theme;
    Context?: React.Context<ThemeContext>;
    autoConnect?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = (props) => {
    const {
        children,
        Context = DefaultThemeContext,
        theme,
        updateBody = false,
        autoConnect = false
    } = props;
    const ref = React.createRef<HTMLDivElement>();
    const [themeState, setThemeState] = React.useState<Theme>(theme ?? getCurrentTheme()?.theme ?? defaultTheme);
    const themeRef = React.useRef<Theme>(themeState);
    themeRef.current = themeState;
    const [ready, setReady] = React.useState(false);
    const {document: documentContext} = React.useContext(WindowContext);

    // Get the initial theme and setup listeners for changes
    React.useEffect(() => {
        let unsubscribe: () => void;
        if (autoConnect) {
            addThemeListener((newTheme) => {
                if (newTheme) {
                    setThemeState(newTheme);
                }
            })
                .then((unsub) => {
                    unsubscribe = unsub;
                });
        }
        return () => {
            unsubscribe?.();
        };
    }, [autoConnect]);

    React.useEffect(() => {
        if (ref.current) {
            applyTheme(ref.current, themeState, updateBody, documentContext);
            setReady(true);
        }
    }, [ref, themeState]);

    const value: ThemeContext = {
        theme: themeRef,
        setTheme: (newThemeState) => {
            setThemeState(newThemeState);
            themeRef.current = newThemeState;
        }
    };

    return (
        <Context.Provider value={value}>
            <div ref={ref} style={{display: 'inherit', height: '100%', width: '100%'}}>{ready && children}</div>
        </Context.Provider>
    );
};

/**
 * Apply a theme config to a DOM node.
 * @param node The DOM node to apply a theme's CSS custom properties to.
 * @param theme Theme to apply.
 * @param updateBody Whether the background color of the underlying document should be updated to match the theme
 */
function applyTheme(node: HTMLElement, theme: Theme, updateBody: boolean, documentContext: Document = document): void {
    console.log(`Applying ${theme.name} | Update body: ${updateBody} | Document:`, documentContext, theme);
    parseThemeConfig(theme, (key, value, prefix) => {
        const propertyName = createPropertyName(key, prefix);
        node.style.setProperty(propertyName, value as string);
    });

    if (updateBody && theme.color?.primary) {
        // @ts-ignore color has different shape in new designs
        documentContext.body.style.background = theme.color.primary;
    }
    console.groupEnd();
}
